// Authentication Context using Azure AD (MSAL)
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';
import API from '../services/api';

interface User {
  id: string;
  email: string;
  name: string;
  displayName: string; 
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserRoles = async () => {
      if (isAuthenticated && accounts.length > 0 && inProgress === InteractionStatus.None) {
        const account = accounts[0];
        
        try {
          // Get access token
          const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account: account,
          });

          // Call API to get current user info with real roles
          const response = await API.get('/users/me', {
            headers: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
            },
          });

          const userData = response.data;
          
          const authenticatedUser: User = {
            id: userData.userId || account.localAccountId,
            email: userData.email || account.username,
            displayName: userData.displayName || account.name || account.username,
            name: userData.displayName || account.name || account.username,
            roles: userData.roles || [], // Real roles from backend
          };
          
          console.log('âœ… User authenticated with roles:', authenticatedUser.roles);
          setUser(authenticatedUser);
        } catch (error) {
          console.error('âŒ Failed to fetch user roles:', error);
          
          // Fallback: set user without roles (will have minimal access)
          const fallbackUser: User = {
            id: account.localAccountId,
            email: account.username,
            displayName: account.name || account.username,
            name: account.name || account.username,
            roles: [], // No roles if API call fails
          };
          
          setUser(fallbackUser);
        } finally {
          setIsLoading(false);
        }
      } else if (!isAuthenticated && inProgress === InteractionStatus.None) {
        setUser(null);
        setIsLoading(false);
      }
    };

    fetchUserRoles();
  }, [isAuthenticated, accounts, inProgress, instance]);

  const login = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await instance.logoutPopup();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const hasRole = (role: string): boolean => {
    return user?.roles.includes(role) || false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};